import React from 'react';
import Link from 'redux-first-router-link';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import cartAnalytics from './cartAnalytics';
import Select from '../Elements/Select/Select';
import { changeQuantity, addToBag } from '../../actions/checkout';
import Price from '../Product/Price';
import { fillArray } from '../../utils/array';
import { translate, translateObjectData, prefix } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import { urlify, atob } from '../../utils/string';
import s from './CartItems.scss';

const CartItems = ({ checkout, currentPage, setup, cart, toggleCartHandler, products, productVariantLookup, ...props }) => {
	const updateQuantityHandler = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const itemId = $link.dataset.item;
		const itemObj = checkout.selection.items.reduce((acc, item) => (item.line === itemId ? { ...acc, ...item } : acc), {});
		const action = $link.getAttribute('href').slice(1);
		let cachedItem = checkout.selectionCache.filter((scItem) => scItem && scItem.selectionVariantId === itemObj.id)[0];
		if (!cachedItem) {
			cachedItem = deepClone(products[itemObj.cacheId]);
			cachedItem.selectionVariantId = itemObj.id;
			cachedItem.category = cachedItem.canonicalUri.slice(0, cachedItem.canonicalUri.lastIndexOf('/'));
		}
		let quantity = itemObj.quantity;

		switch (action) {
			case 'increase':
				quantity += 1;
				break;
			case 'decrease':
				quantity -= 1;
				break;
			case 'remove':
				quantity = 0;
				break;
			default:
				break;
		}

		props.changeQuantity([{ id: itemObj.line, quantity }], cachedItem, cachedItem.category).then((response) => {
			cartAnalytics(itemObj.id, products, productVariantLookup, setup, cachedItem.category, response.data, checkout.listItemCache, ['remove', 'decrease'].indexOf(action) !== -1, quantity);
			return response;
		}).catch((err) => console.log('ERROR: Cart update analytics errored. ', err));
	};

	const setQuantityHandler = (id, quantity) => {
		const itemId = id.replace('updateQuantity-', '');
		const itemObj = checkout.selection.items.reduce((acc, item) => (item.line === itemId ? { ...acc, ...item } : acc), {});
		let cachedItem = checkout.selectionCache.filter((scItem) => scItem && scItem.selectionVariantId === itemObj.id)[0];
		if (!cachedItem) {
			cachedItem = deepClone(products[itemObj.cacheId]);
			cachedItem.selectionVariantId = itemObj.id;
			cachedItem.category = cachedItem.canonicalUri.slice(0, cachedItem.canonicalUri.lastIndexOf('/'));
		}
		quantity = parseInt(quantity, 10);

		props.changeQuantity([{ id: itemObj.line, quantity }], cachedItem, cachedItem.category).then((response) => {
			cartAnalytics(itemObj.id, products, productVariantLookup, setup, cachedItem.category, response.data, checkout.listItemCache, cachedItem.quantity < quantity, quantity);
			return response;
		}).catch((err) => console.log('ERROR: Cart update analytics errored. ', err));
	};

	const lineItems = checkout.selection.items ? checkout.selection.items
		.sort((a, b) => {
			let aIndex = 0;
			let bIndex = 0;
			checkout.selectionCache.forEach((item, i) => {
				if (!item) return;
				if (a.id === item.selectionVariantId) aIndex = i;
				if (b.id === item.selectionVariantId) bIndex = i;
			});
			if (aIndex === 0 && bIndex === 0) return 0;
			return aIndex > bIndex ? 1 : -1;
		})
		.map((item) => {
			let itemCache = checkout.selectionCache.filter((scItem) => scItem && atob(scItem.selectionVariantId) === atob(item.id))[0];
			if (!itemCache?.name) itemCache = products[item.cacheId];
			if (item.quantity === 0 || !itemCache?.name) {
				return null;
			}
			const itemDetails = translateObjectData(setup, itemCache);
			const weightParts = (itemDetails?.weight || '').split('\u212e');

			return (
				<div key={item.line} className={[s.cartItem, 'checkoutItem'].join(' ')}>
					<Link className={s.cartItem_image} to={prefix(setup, itemDetails.canonicalUri)} onClick={toggleCartHandler}>
						{ itemDetails.media.filter((mediaItem) => mediaItem.type === 'IMAGE').slice(0, 1).map((mediaItem) => <img key={mediaItem.src} src={mediaItem.src.replace(/\.([^.]+)$/, '_200x.$1')} alt={mediaItem.alt} />) }
					</Link>
					<div className={s.cartItem_info}>
						<div>
							<span className={s.cartItem_info_name}>{ itemDetails.name } <br /> { itemDetails.variant }</span>
							{
								weightParts?.[0] && (
									<span className={s.cartItem_info_weight}>
										{ weightParts[0] }
										{ weightParts?.[1] && <span className={s.cartItem_info_weight_symbol}>\u212e</span> }
										{ weightParts?.[1] }
									</span>
								)
							}
							{ ['', 'os', 'one-size', 'onesize'].indexOf(urlify(item.size)) === -1 && <span className={s.cartItem_info_size}>{ translate(setup, 'size') }: { item.size }</span> }
						</div>
						{
							currentPage !== 'receipt' ? (
								<div className={s.cartItem_info_changeQuantity_wrapper}>
									<div className={s.cartItem_info_changeQuantity}>
										<Select variantClasses={['select__cart', 'label__cart']} id={`updateQuantity-${item.line}`} options={fillArray(11).map((i, j) => j)} selected={item.quantity} inputChange={setQuantityHandler} label={translate(setup, cart.quantity)} />
									</div>
								</div>
							) : <span className={s.cartItem_info_changeQuantity_quantity}>{ item.quantity }</span>
						}
					</div>
					<a className={s.cartItem_remove} href="#remove" onClick={updateQuantityHandler} data-item={item.line}>
						<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<line x1="0.106066" y1="0.893934" x2="14.1061" y2="14.8939" stroke="black" strokeWidth="0.5" />
							<line x1="14.107" y1="1.10607" x2="0.107043" y2="15.1061" stroke="black" strokeWidth="0.5" />
						</svg>
					</a>
					<span className={s.cartItem_price}><Price price={item?.price?.[setup.currency] ? item.price : itemCache.price} setup={setup} checkout /></span>
				</div>
			);
		}) : [];

	return (
		<div className={s.cartItems}>
			{ checkout.selection.items && lineItems }
		</div>
	);
};

const mapState = ({ environment, checkout, productCache }, props) => ({
	checkout,
	setup: environment.setup,
	cart: environment.cart,
	productCache,
	...props,
});

export default hot(module)(connect(mapState, { changeQuantity, addToBag })(CartItems));
